import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, MessageSquare, Check } from "lucide-react";

// Blocking popup for newly received messages — the recipient must acknowledge
// (read) each one before continuing. Mount once per app surface.
export function MessageAckPopup({ enabled = true }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showOrig, setShowOrig] = useState(false);

  const { data } = useQuery({
    queryKey: ["messages-unread-list"],
    queryFn: async () => (await api.get("/messages/unread")).data,
    refetchInterval: 15000,
    enabled,
  });

  const messages = data?.messages || [];
  const current = messages[0];

  const ack = useMutation({
    mutationFn: async (id) => (await api.post("/messages/ack", { message_ids: [id] })).data,
    onSuccess: () => {
      setShowOrig(false);
      qc.invalidateQueries({ queryKey: ["messages-unread-list"] });
      qc.invalidateQueries({ queryKey: ["messages-unread-count"] });
    },
  });

  if (!current) return null;

  const remaining = messages.length;
  const when = current.created_at
    ? new Date(current.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <Dialog open>
      <DialogContent
        className="max-w-sm [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="message-ack-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-indigo-500" />
            {t("New message")}
            {remaining > 1 && (
              <span className="ml-auto rounded-full bg-indigo-100 px-2 text-xs font-bold text-indigo-700" data-testid="ack-remaining">
                {remaining}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {current.sender_name}{when ? ` · ${when}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg bg-secondary/60 p-3 text-[15px] leading-relaxed" data-testid="ack-message-text">
          {showOrig ? current.original_text : current.text}
        </div>
        {current.translated && (
          <button
            onClick={() => setShowOrig((v) => !v)}
            className="k-press -mt-1 w-fit text-xs font-medium text-muted-foreground underline"
            data-testid="ack-toggle-orig"
          >
            {showOrig ? t("Hide original") : t("Translated · show original")}
          </button>
        )}

        <DialogFooter>
          <Button
            onClick={() => ack.mutate(current.id)}
            disabled={ack.isPending}
            data-testid="ack-got-it-btn"
            className="h-12 w-full gap-2 text-base font-bold"
          >
            {ack.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
            {t("Got it")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
